import {
  Avatar,
  Box,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  Typography,
} from '@mui/material';
import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  markAssignedNotificationsAsRead,
  viewAllNotificationsByEmpId,
} from 'service';
import { strings } from 'i18n/Strings';
import { type RootState } from 'store';
import { formatNotificationDate } from 'components/helpers/commonFunctions/NotificationDateCalculation';
import io, { type Socket } from 'socket.io-client';
import { getAccessToken } from 'service/AuthService';
import ApiUtils from 'utils/ApiUtil';
import { setUnreadCount } from 'store/notifications';

interface NotificationProps {
  anchorEl: unknown;
  handleClose: () => void;
  open: boolean;
}
interface Notification {
  id: number;
  description: string;
  time: string;
  isRead: boolean;
  type: string;
  referenceId: number;
  userId: number;
  firstName: string;
  lastName: string;
}
interface SocketData {
  senderId: number;
  text: {
    text: string;
    image?: string;
  };
}

const Notifications = (props: NotificationProps): JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const socketRef = useRef<Socket | null>(null);

  const { empId } = useSelector((state: RootState) => state.onboarding);
  const { anchorEl, handleClose, open } = props;
  const [notifications, setNotifications] = useState([]);
  const id = open ? 'popover-notification' : undefined;

  const accessToken = getAccessToken();
  useEffect(() => {
    if (empId) {
      socketRef.current = io(process.env.REACT_APP_MARSHMALLOW_API, {
        auth: {
          token: accessToken, // JWT
        },
      });
      socketRef.current.emit('addAdmin', empId);

      socketRef.current.on('getAllNotifications', (data: SocketData) => {
        setNotifications(data?.allNotificationsNewToOld);
        dispatch(setUnreadCount(data?.unreadCount));
      });

      socketRef.current.on('connect_error', async (err) => {
        console.log('Socket connection error:', err.message);
        if (
          err.message === 'TOKEN_EXPIRED' ||
          err.message === 'INVALID_TOKEN'
        ) {
          try {
            // Refresh via HTTP
            const response = await ApiUtils.refreshToken();

            const newAccessToken = response.accessToken;

            // Save new token
            localStorage.setItem('accessToken', newAccessToken);

            // Update socket auth
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            socketRef.current!.auth = {
              token: newAccessToken,
            };

            // Reconnect
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            socketRef.current!.connect();
          } catch (e) {
            socketRef.current?.disconnect();

            // logout user
          }
        }
      });
    }
  }, [empId]);

  const getAllNotifications = async (): Promise<void> => {
    try {
      const notification = await viewAllNotificationsByEmpId(empId);
      setNotifications(notification?.allNotificationsNewToOld);
      dispatch(setUnreadCount(notification?.unreadCount));
    } catch (error) {
      console.log(error);
    }
  };

  const markNotificationReadOnClick = async (id: number): Promise<void> => {
    try {
      await markAssignedNotificationsAsRead(id);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    void getAllNotifications();
  }, [empId]);

  const handleNotificationClick = async (
    id: number,
    referenceId: number,
    userId: number,
    type: string,
  ): Promise<void> => {
    handleClose();
    switch (type) {
      case 'chat':
        navigate(`/customer-chat/${userId}`);
        break;
      case 'complaint':
        navigate(`/customer-complaints/${referenceId}`);
        break;
      case 'withdraw request':
        navigate(`/user-request-details/${referenceId}`);
        break;
      case 'plan request':
        navigate(`/requests`);
        break;
      case 'replacing policy request':
        navigate('/onboarding');
        break;
      default:
        break;
    }
    await markNotificationReadOnClick(id);
    await getAllNotifications();
  };

  return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <>
        <Typography sx={{ m: 1, p: 1, fontSize: 24, fontWeight: 'bold' }}>
          {strings.displayText.notifications}
        </Typography>
        <List
          sx={{
            maxHeight: '85vh',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
            },
          }}
        >
          {notifications.map((notification: Notification, index) => (
            <React.Fragment key={notification.id}>
              <ListItem
                alignItems="flex-start"
                button
                onClick={async () => {
                  await handleNotificationClick(
                    notification.id,
                    notification.referenceId,
                    notification.userId,
                    notification.type,
                  );
                }}
                sx={{
                  marginTop: 1,
                  marginBottom: 1,
                  backgroundColor: notification.isRead
                    ? 'transparent'
                    : '#B5DFC8',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    alt={notification.firstName + ' ' + notification.lastName}
                    src="/path/to/image.png"
                  />
                </ListItemAvatar>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    flexGrow: 1,
                    overflow: 'hidden',
                    maxWidth: 300,
                  }}
                >
                  <ListItemText>
                    <Typography
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {notification.firstName} {notification.lastName}
                    </Typography>
                  </ListItemText>
                  <ListItemText>
                    <Typography
                      component="span"
                      variant="body2"
                      color="textPrimary"
                      display="block"
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {notification.description}
                    </Typography>
                  </ListItemText>
                </Box>
                <Typography
                  component="span"
                  variant="caption"
                  color="textSecondary"
                >
                  {formatNotificationDate(notification.time)}
                </Typography>
              </ListItem>
              {index !== notifications.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </>
    </Popover>
  );
};

export default Notifications;
